import { supabase } from '../lib/supabase';

class SupabaseConnectionService {
  constructor() {
    this.isChecking = false;
    this.lastCheckTime = null;
    this.connectionCache = {
      status: 'checking',
      timestamp: Date.now(),
      details: null
    };
  }

  /**
   * Check Supabase database connectivity
   * @returns {Promise<Object>} Connection status object
   */
  async checkConnection() {
    if (this.isChecking) {
      return this.connectionCache;
    }

    this.isChecking = true;
    const startTime = Date.now();

    try {
      // Test database connectivity with a simple query
      const { data, error } = await supabase?.from('user_profiles')?.select('id')?.limit(1);

      const responseTime = Date.now() - startTime;

      if (error) {
        this.connectionCache = {
          status: 'disconnected',
          timestamp: Date.now(),
          details: {
            error: error?.message,
            code: error?.code,
            responseTime
          }
        };
      } else {
        this.connectionCache = {
          status: 'connected',
          timestamp: Date.now(),
          details: {
            responseTime,
            recordsAccessible: data !== null
          }
        };
      }
    } catch (error) {
      this.connectionCache = {
        status: 'disconnected',
        timestamp: Date.now(),
        details: {
          error: error?.message,
          type: 'network_error',
          responseTime: Date.now() - startTime
        }
      };
    } finally {
      this.isChecking = false;
      this.lastCheckTime = Date.now();
    }

    return this.connectionCache;
  }

  /**
   * Get cached connection status if recent, otherwise perform new check
   * @param {number} maxAge - Maximum age of cache in milliseconds (default: 30 seconds)
   * @returns {Promise<Object>} Connection status object
   */
  async getConnectionStatus(maxAge = 30000) {
    const now = Date.now();
    
    if (this.lastCheckTime && (now - this.lastCheckTime) < maxAge) {
      return this.connectionCache;
    }

    return await this.checkConnection();
  }

  /**
   * Start monitoring connection status with periodic checks
   * @param {Function} callback - Callback function to receive status updates
   * @param {number} interval - Check interval in milliseconds (default: 30 seconds)
   * @returns {Function} Cleanup function to stop monitoring
   */
  startMonitoring(callback, interval = 30000) {
    // Initial check
    this.getConnectionStatus()?.then(callback);

    // Set up periodic checks
    const intervalId = setInterval(async () => {
      const status = await this.checkConnection();
      callback(status);
    }, interval);

    // Return cleanup function
    return () => clearInterval(intervalId);
  }

  /**
   * Test authentication connectivity
   * @returns {Promise<Object>} Auth status object
   */
  async checkAuthConnection() {
    try {
      const { data: { session }, error } = await supabase?.auth?.getSession();
      
      return {
        status: error ? 'error' : 'connected',
        hasSession: !!session,
        error: error?.message
      };
    } catch (error) {
      return {
        status: 'error',
        hasSession: false,
        error: error?.message
      };
    }
  }

  /**
   * Get comprehensive connection health information
   * @returns {Promise<Object>} Detailed health check object
   */
  async getHealthCheck() {
    const [dbStatus, authStatus] = await Promise.all([
      this.getConnectionStatus(),
      this.checkAuthConnection()
    ]);

    return {
      database: dbStatus,
      authentication: authStatus,
      overall: dbStatus?.status === 'connected' && authStatus?.status === 'connected' ?'healthy' :'degraded',
      timestamp: Date.now()
    };
  }
}

// Export singleton instance
export const supabaseConnectionService = new SupabaseConnectionService();
export default supabaseConnectionService;